import React, { useState, useEffect } from 'react'
import { Radio, Card, message, Input, Form, Button } from 'antd'
import CommonCombinedFilter from '../../../../../components/CommonCombinedFilter'
import { getQualityCheckTaskList } from '@services/ai-quality-check'
import { useParams } from 'react-router-dom'
import { CombinedFilterItemDataType, AIQualityCheckTaskCreateFilter, AIQualityCheckTaskListItem } from 'src/types/ai-quality-check'
import { offlineOptionList, onlineOptionList } from '../../../../../components/CommonCombinedFilter/option'
import { DataType, ActionMode, StatusSteps } from '../../../quality-check-scheme-detail/constants'
import { getUrlParam } from '@library'

const runTypeMode = {
  offlineAsync: 1,
  onlineSync: 2,
  onlineAsync: 3,
}

const ChooseRecordPage = ({ onSave, onNextStep, createId }: { onSave: (data: AIQualityCheckTaskCreateFilter, status?: number, savedId?: number) => void, onNextStep: (data: AIQualityCheckTaskCreateFilter, status?: number, savedId?: number) => void, createId: number }) => {
  const [isCombinedFilterDataVaild, setIsCombinedFilterDataVaild] = useState(false)
  const [combinedForm, setCombinedForm] = useState([]) // 存储表单数据, 防止表单循环更新
  const [savedData, setSavedData] = useState<AIQualityCheckTaskListItem>({}) // 已保存数据
  const [stepOneData, setStepOneData] = useState({})
  const taskId = +useParams<{ id: string }>()?.id
  const status = Number(getUrlParam('status'))
  const [form] = Form.useForm()
  const optionList = savedData.taskType === ActionMode.online ? onlineOptionList : offlineOptionList

  const getQualityCheckTaskCreateData = async () => {
    const params = {
      taskId: taskId || createId,
    }
    const [res, err] = await getQualityCheckTaskList(params)
    if (err) {
      message.error(`获取质检任务数据失败:${err?.message}`)
      return
    }
    const { taskName, dataType, taskType, dataScope, runType } = res?.list?.[0] || {}
    if (dataScope) {
      try {
        const data = JSON.parse(dataScope)
        setSavedData({
          ...res?.list?.[0],
          formData: data.conditions,
        })
        setCombinedForm(data.conditions)
      } catch (error) {
        message.error('解析数据范围失败')
      }
    } else {
      setSavedData(res?.list?.[0])
    }

    let newTaskType = taskType
    if (taskType === ActionMode.online) {
      if (runType === 0) {
        newTaskType = runTypeMode.onlineSync
      }
      if (runType === 1) {
        newTaskType = runTypeMode.onlineAsync
      }
    }
    form.setFieldsValue({
      taskName: taskName,
      dataType: dataType,
      actionMode: newTaskType,
    })
  }

  // 对表单值进行特殊处理，以满足后端接口的要求
  const getItemValue = (item: CombinedFilterItemDataType) => {
    const config = optionList.find(option => option.value === item.value.fieldName)
    const isCombined = config?.isCombined || false
    const isDuration = config?.isDuration || false
    const isArray = config?.isArray || false
    if (isArray) {
      return handleArrayValue(item?.value?.rangeValue)
    }
    if (isDuration) {
      // 时间区间类型支持单个值，需要做去空处理
      const rangeValue = item.value.rangeValue as number[]
      const realValues = rangeValue?.filter(val => val !== null && val !== undefined)
      return realValues?.length === 2 ? rangeValue : realValues?.[0]
    }
    return isCombined ? item.value.rangeValue?.[1] : item.value.rangeValue
  }

  // 对表单操作符进行特殊处理，以满足后端接口的要求
  const getItemOperator = (item: CombinedFilterItemDataType) => {
    const config = optionList.find(option => option.value === item.value.fieldName)
    const isCombined = config?.isCombined || false
    const isDuration = config?.isDuration || false
    if (isDuration) {
      // 时间区间类型，根据值数量判断是大于等于还是小于等于
      const realValues = (item.value.rangeValue as number[])?.filter(val => val !== null && val !== undefined)
      if (realValues?.length === 1) {
        return item.value.rangeValue[0] !== null && item.value.rangeValue[0] !== undefined ? 'ge' : 'le'
      }
      return config?.supportOperators[0]
    }
    return isCombined ? item.value.rangeValue?.[0] : config?.supportOperators[0]
  }

  // 特殊处理级联类型的值，以满足后端要求
  const handleArrayValue = (value) => {
    const keyWord = value?.flatMap((item) => {
      if (item.length === 2) {
        // 非全选时，按_分割取出单个关键词
        return item[1].split('_')[0]
      } else if (item.length === 1) {
        // 全选时，截取数组内容并分割关键词
        const startIndex = item[0].indexOf('[')
        const endIndex = item[0].lastIndexOf(']')
        if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
          const content = item[0].slice(startIndex + 1, endIndex)
          return content.split(',').map(str => str.replace(/^"|"$/g, ''))
        }
      }
      return []
    })
    return keyWord
  }

  const handleCombinedFilterData = (value: CombinedFilterItemDataType[]) => {
    const result = value?.map((item: CombinedFilterItemDataType) => {
      return {
        fieldName: item.value.fieldName,
        value: getItemValue(item),
        op: getItemOperator(item),
        label: item.value.label,
      }
    })
    setCombinedForm(result)
    setStepOneData({
      dataScope: JSON.stringify({
        conditions: result,
        logic: 'AND',
      }),
      dataType: savedData?.dataType,
      taskType: savedData?.taskType,
      taskName: savedData?.taskName,
      runType: savedData?.runType,
      ...(status === StatusSteps.STEP_TWO || status === StatusSteps.STEP_THREE ? { planIds: savedData?.planIds, tagPlanId: savedData?.tagPlanId } : {}),
      ...(status === StatusSteps.STEP_THREE ? { manualReview: savedData?.manualReview } : {}),
    })
  }

  const getFormValue = (value: CombinedFilterItemDataType[], isValid: boolean) => {
    handleCombinedFilterData(value)
    setIsCombinedFilterDataVaild(isValid)
  }

  const handleSaveDraft = () => {
    onSave(stepOneData, null, createId)
  }

  const handleNextStep = () => {
    onNextStep(stepOneData, null, createId)
  }

  useEffect(() => {
    form.setFieldsValue({
      taskName: null,
      dataType: DataType.call,
      actionMode: runTypeMode.offlineAsync,
    })
  }, [])

  useEffect(() => {
    if (!isNaN(taskId) || createId) {
      getQualityCheckTaskCreateData()
    }
  }, [taskId, createId])

  useEffect(() => {
    setStepOneData({
      dataScope: JSON.stringify({
        conditions: combinedForm,
        logic: 'AND',
      }),
      dataType: savedData?.dataType,
      taskType: savedData?.taskType,
      taskName: savedData?.taskName,
      runType: savedData?.runType,
      ...(status === StatusSteps.STEP_TWO || status === StatusSteps.STEP_THREE || savedData?.planIds || savedData?.tagPlanId ? { planIds: savedData?.planIds, tagPlanId: savedData?.tagPlanId } : {}),
      ...(status === StatusSteps.STEP_THREE || savedData?.manualReview ? { manualReview: savedData?.manualReview } : {}),
    })
  }, [savedData, combinedForm])

  useEffect(() => {
    form.validateFields()
  }, [savedData.taskName, isCombinedFilterDataVaild, form])

  return (
    <Card className="mt20 mb20">
      <div style={{ width: '90%', margin: '0 auto' }}>
        <Form
          form={form}
          className="mt20"
          labelCol={{ span: 2 }}
          wrapperCol={{ span: 22 }}
          initialValues={{
            taskName: savedData?.taskName,
            dataType: savedData?.dataType,
            actionMode: savedData?.taskType,
            runType: savedData?.runType,
          }}
          onValuesChange={(_, values) => {
            // 当选择存量数据时，taskType传1， runType不传
            // 当选择增量数据时，taskType传2， runType根据选的实时任务还是异步任务来区分传0还是1
            let runType
            if (values.actionMode === runTypeMode.offlineAsync) {
              runType = undefined
            } else {
              if (values.actionMode === runTypeMode.onlineSync) {
                runType = 0
              } else {
                runType = 1
              }
            }
            setSavedData({
              ...savedData,
              taskName: values.taskName,
              dataType: values.dataType,
              taskType: values.actionMode === runTypeMode.offlineAsync ? ActionMode.offline : ActionMode.online,
              runType,
            })
          }}
        >
          <Form.Item
            label="任务名称"
            labelAlign="left"
            labelCol={{ style: { fontWeight: 'bold' } }}
            name="taskName"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input
              placeholder="请输入任务名称（如：5月红线质检）"
            />
          </Form.Item>
          <Form.Item
            label="数据类型"
            labelAlign="left"
            labelCol={{ style: { fontWeight: 'bold' } }}
            name="dataType"
            rules={[{ required: true, message: '请选择数据类型' }]}
          >
            <Radio.Group>
              <Radio value={DataType.call}>电话通话</Radio>
              <Radio value={DataType.wechat} disabled>企微对话（待上线）</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="运行模式"
            labelAlign="left"
            labelCol={{ style: { fontWeight: 'bold' } }}
            name="actionMode"
            rules={[{ required: true, message: '请选择运行模式' }]}
            initialValue={runTypeMode.offlineAsync}
          >
            <Radio.Group>
              <div style={{ marginTop: 5 }}>
                <div style={{ fontSize: '14px', fontFamily: 'PingFang SC', fontWeight: 400, color: '#000000', marginBottom: 8 }}>
                  存量数据<span style={{ color: '#B5BECB', fontSize: 12 }}>（当前已有的，在通话列表中有记录的通话数据）</span>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Radio value={runTypeMode.offlineAsync}>异步任务</Radio>
                  <span style={{ color: '#B5BECB', fontSize: 12, marginLeft: 8 }}>（对筛选范围内的所有通话进行统一质检）</span>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: '14px', fontFamily: 'PingFang SC', fontWeight: 400, color: '#000000', marginBottom: 8 }}>
                  增量数据<span style={{ color: '#B5BECB', fontSize: 12 }}>（在未来某段时间内产生的通话数据）</span>
                </div>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Radio value={runTypeMode.onlineSync}>实时任务</Radio>
                    <span style={{ color: '#B5BECB', fontSize: 12, marginLeft: 8 }}>（产生通话记录后立即质检/产生质检结果）</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Radio value={runTypeMode.onlineAsync}>异步任务</Radio>
                    <span style={{ color: '#B5BECB', fontSize: 12, marginLeft: 8 }}>（在晚间对当天所有通话进行统一质检，第二天产生质检结果）</span>
                  </div>
                </div>
              </div>
            </Radio.Group>
          </Form.Item>
        </Form>
        <div className="mt20 mb20">
          <h4 style={{ fontWeight: 'bold' }}>数据匹配条件</h4>
          <CommonCombinedFilter className="mt20" filterType={savedData?.formData?.length === 0 ? 'create' : 'edit'} data={savedData?.formData} actionMode={savedData?.taskType} exportFormValue={getFormValue} />
        </div>
      </div>
      <div className="mt20 mb20" style={{ display: 'flex' }}>
        <Button type="primary" className="mr10" style={{ backgroundColor: '#0052D9' }} onClick={handleNextStep}>下一步</Button>
        <Button type="primary" className="task-save-btn" onClick={handleSaveDraft}>
          保存草稿
        </Button>
      </div>
    </Card>
  )
}

export default ChooseRecordPage
